import { getActiveTenantKey, getTenantScopedKey } from '../offline/tenant'
import { type ReportRow, normalizeReportScope, type ReportScopeV1 } from './reportScope'
import type { ReportDatasetKey, ReportDefinitionV1 } from './reportStorage'
import { loadJSON, saveJSON } from './storage'

export type ReportSnapshotArtifactType = 'report-snapshot'
export type ArtifactVisibility = 'private' | 'group' | 'agora'
export type ArtifactStatus = 'draft' | 'published' | 'archived' | 'revoked'
export type ArtifactVersionStatus = 'published' | 'superseded' | 'revoked'

export type ReportSnapshotColumn = {
  key: string
  label: string
}

export type ReportSnapshotArtifactSummary = {
  artifactId: string
  artifactType: ReportSnapshotArtifactType
  title: string
  contextLine: string
  summary: string
  visibility: ArtifactVisibility
  status: ArtifactStatus
  currentVersion: number
  ownerTenantKey: string
  publishedAt: string | null
  updatedAt: string
  sourceLabel: string
  sourceHref: string
  highlights: string[]
}

export type ReportSnapshotArtifactPreview = {
  datasetKey: ReportDatasetKey
  datasetLabel: string
  columns: ReportSnapshotColumn[]
  scope: ReportScopeV1 | null
  generatedAt: string
  rowCount: number
  sampleRows: ReportRow[]
}

export type ReportSnapshotArtifactVersion = {
  version: number
  status: ArtifactVersionStatus
  publishedAt: string | null
  revokedAt: string | null
  generatedAt: string
  snapshotPreview: ReportSnapshotArtifactPreview
}

type ArtifactLifecycleEvent = {
  type: 'published' | 'revoked' | 'archived'
  at: string
  version: number | null
}

export type ReportSnapshotArtifactDetail = {
  artifactId: string
  artifactType: ReportSnapshotArtifactType
  overview: Omit<ReportSnapshotArtifactSummary, 'artifactId' | 'artifactType' | 'highlights' | 'sourceLabel' | 'sourceHref'>
  snapshotPreview: ReportSnapshotArtifactPreview
  provenance: {
    sourceRefs: Array<{
      domain: 'reports'
      entityId: string
      label: string
    }>
    publishedBy: {
      tenantKey: string
      displayName: string
    } | null
    lifecycleEvents: ArtifactLifecycleEvent[]
  }
  versions: Array<{
    version: number
    status: ArtifactVersionStatus
    publishedAt: string | null
  }>
  source: {
    label: string
    href: string
  }
  actions: {
    canCreateDraft: boolean
    canRefreshDraftSnapshot: boolean
    canPublish: boolean
    canRevoke: boolean
    canArchive: boolean
  }
}

export type PublishReportSnapshotArtifactInput = {
  report: ReportDefinitionV1
  datasetLabel: string
  columns: ReportSnapshotColumn[]
  rows: ReportRow[]
  sourceHref?: string
  publisherDisplayName?: string | null
  generatedAt?: string
  tenantKey?: string
}

type StoredReportSnapshotArtifact = ReportSnapshotArtifactSummary & {
  version: 1
  sourceReportId: string
  datasetKey: ReportDatasetKey
  datasetLabel: string
  publisherDisplayName: string | null
  archivedAt: string | null
  versions: ReportSnapshotArtifactVersion[]
}

type ReportSnapshotArtifactsPayload = {
  version: 1
  items: StoredReportSnapshotArtifact[]
}

const REPORT_SNAPSHOT_ARTIFACTS_KEY = 'collaboration:artifacts:report-snapshots:v1'
const DATASET_KEYS = new Set<ReportDatasetKey>([
  'tasks',
  'inventory',
  'productionDatatable',
  'water',
  'lots',
  'feed',
  'feedEvents',
  'feedDailyTotals'
])

const artifactsKey = (tenantKey = getActiveTenantKey()): string => (
  getTenantScopedKey(REPORT_SNAPSHOT_ARTIFACTS_KEY, tenantKey)
)

const toTrimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const normalizeStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
}

const isDatasetKey = (value: unknown): value is ReportDatasetKey => (
  typeof value === 'string' && DATASET_KEYS.has(value as ReportDatasetKey)
)

const isArtifactVisibility = (value: unknown): value is ArtifactVisibility => (
  value === 'private' || value === 'group' || value === 'agora'
)

const isArtifactStatus = (value: unknown): value is ArtifactStatus => (
  value === 'draft' || value === 'published' || value === 'archived' || value === 'revoked'
)

const isArtifactVersionStatus = (value: unknown): value is ArtifactVersionStatus => (
  value === 'published' || value === 'superseded' || value === 'revoked'
)

const normalizeColumns = (value: unknown): ReportSnapshotColumn[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((candidate) => {
      if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null
      const record = candidate as Record<string, unknown>
      const key = toTrimmedString(record.key)
      const label = toTrimmedString(record.label)
      if (!key || !label) return null
      return { key, label }
    })
    .filter((item): item is ReportSnapshotColumn => Boolean(item))
}

const cloneRows = (rows: ReportRow[]): ReportRow[] => JSON.parse(JSON.stringify(rows)) as ReportRow[]

const normalizeRows = (value: unknown): ReportRow[] => {
  if (!Array.isArray(value)) return []
  return cloneRows(value.filter((item): item is ReportRow => Boolean(item) && typeof item === 'object' && !Array.isArray(item)))
}

const normalizeSnapshotPreview = (value: unknown): ReportSnapshotArtifactPreview | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>

  if (!isDatasetKey(record.datasetKey)) return null
  const datasetLabel = toTrimmedString(record.datasetLabel)
  const columns = normalizeColumns(record.columns)
  const generatedAt = toTrimmedString(record.generatedAt)
  const rowCount = typeof record.rowCount === 'number' && Number.isFinite(record.rowCount)
    ? record.rowCount
    : null

  if (!datasetLabel || !generatedAt || rowCount === null) return null

  return {
    datasetKey: record.datasetKey,
    datasetLabel,
    columns,
    scope: normalizeReportScope(record.scope),
    generatedAt,
    rowCount,
    sampleRows: normalizeRows(record.sampleRows)
  }
}

const normalizeVersion = (value: unknown): ReportSnapshotArtifactVersion | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>

  const version = typeof record.version === 'number' && Number.isInteger(record.version) && record.version > 0
    ? record.version
    : null
  const publishedAt = record.publishedAt === null ? null : toTrimmedString(record.publishedAt)
  const revokedAt = record.revokedAt === null ? null : toTrimmedString(record.revokedAt)
  const generatedAt = toTrimmedString(record.generatedAt)
  const snapshotPreview = normalizeSnapshotPreview(record.snapshotPreview)

  if (version === null || !isArtifactVersionStatus(record.status) || !generatedAt || !snapshotPreview) {
    return null
  }

  return {
    version,
    status: record.status,
    publishedAt,
    revokedAt,
    generatedAt,
    snapshotPreview
  }
}

const normalizeStoredArtifact = (candidate: unknown, tenantKey: string): StoredReportSnapshotArtifact | null => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null
  const record = candidate as Record<string, unknown>

  const artifactId = toTrimmedString(record.artifactId)
  const title = toTrimmedString(record.title)
  const contextLine = toTrimmedString(record.contextLine)
  const summary = toTrimmedString(record.summary)
  const ownerTenantKey = toTrimmedString(record.ownerTenantKey)
  const updatedAt = toTrimmedString(record.updatedAt)
  const sourceLabel = toTrimmedString(record.sourceLabel)
  const sourceHref = toTrimmedString(record.sourceHref)
  const sourceReportId = toTrimmedString(record.sourceReportId)
  const datasetLabel = toTrimmedString(record.datasetLabel)
  const publishedAt = record.publishedAt === null ? null : toTrimmedString(record.publishedAt)
  const archivedAt = record.archivedAt === null ? null : toTrimmedString(record.archivedAt)
  const currentVersion = typeof record.currentVersion === 'number' && Number.isInteger(record.currentVersion)
    ? record.currentVersion
    : null
  const versions = Array.isArray(record.versions)
    ? record.versions.map((item) => normalizeVersion(item)).filter((item): item is ReportSnapshotArtifactVersion => Boolean(item))
    : []

  if (
    record.version !== 1
    || record.artifactType !== 'report-snapshot'
    || !artifactId
    || !title
    || !contextLine
    || !summary
    || !isArtifactVisibility(record.visibility)
    || !isArtifactStatus(record.status)
    || currentVersion === null
    || !ownerTenantKey
    || !updatedAt
    || !sourceLabel
    || !sourceHref
    || !sourceReportId
    || !isDatasetKey(record.datasetKey)
    || !datasetLabel
    || versions.length === 0
  ) {
    return null
  }

  return {
    version: 1,
    artifactId,
    artifactType: 'report-snapshot',
    title,
    contextLine,
    summary,
    visibility: record.visibility,
    status: record.status,
    currentVersion,
    ownerTenantKey: ownerTenantKey || tenantKey,
    publishedAt,
    updatedAt,
    sourceLabel,
    sourceHref,
    highlights: normalizeStringList(record.highlights),
    sourceReportId,
    datasetKey: record.datasetKey,
    datasetLabel,
    publisherDisplayName: toTrimmedString(record.publisherDisplayName),
    archivedAt,
    versions
  }
}

const loadStoredArtifacts = (tenantKey = getActiveTenantKey()): StoredReportSnapshotArtifact[] => {
  const payload = loadJSON<ReportSnapshotArtifactsPayload>(artifactsKey(tenantKey), { version: 1, items: [] })
  const items = (payload.items ?? [])
    .map((item) => normalizeStoredArtifact(item, tenantKey))
    .filter((item): item is StoredReportSnapshotArtifact => Boolean(item))

  return items.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
}

const saveArtifacts = (items: StoredReportSnapshotArtifact[], tenantKey = getActiveTenantKey()): void => {
  saveJSON<ReportSnapshotArtifactsPayload>(artifactsKey(tenantKey), {
    version: 1,
    items
  })
}

const createArtifactId = (reportId: string): string => `report-snapshot-${reportId}`

const formatScopeRange = (scope: ReportScopeV1 | null | undefined): string | null => {
  if (!scope) return null
  if (scope.fromDate && scope.toDate) return `${scope.fromDate} to ${scope.toDate}`
  if (scope.fromDate) return `From ${scope.fromDate}`
  if (scope.toDate) return `Until ${scope.toDate}`
  return null
}

const formatScopeCount = (scope: ReportScopeV1 | null | undefined): string | null => {
  if (!scope) return null
  if (scope.farmIds.length > 0) return `${scope.farmIds.length} ${scope.farmIds.length === 1 ? 'facility' : 'facilities'}`
  if (scope.productionUnitIds.length > 0) return `${scope.productionUnitIds.length} ${scope.productionUnitIds.length === 1 ? 'production unit' : 'production units'}`
  if (scope.lotIds.length > 0 || scope.lotKeys.length > 0) {
    const count = Math.max(scope.lotIds.length, scope.lotKeys.length)
    return `${count} ${count === 1 ? 'lot' : 'lots'}`
  }
  return null
}

const buildContextLine = (report: ReportDefinitionV1, datasetLabel: string): string => {
  const parts = [`${datasetLabel} dataset`]
  const scopeRange = formatScopeRange(report.scope)
  const scopeCount = formatScopeCount(report.scope)
  if (scopeRange) parts.push(scopeRange)
  if (scopeCount) parts.push(scopeCount)
  return parts.join(' · ')
}

const buildSummary = (datasetLabel: string, rowCount: number): string => (
  `Saved ${datasetLabel.toLowerCase()} report snapshot with ${rowCount} ${rowCount === 1 ? 'row' : 'rows'}.`
)

const getArtifactVisibilityLabel = (visibility: ArtifactVisibility): string => {
  switch (visibility) {
    case 'private':
      return 'Private'
    case 'group':
      return 'Group'
    case 'agora':
      return 'Agora'
    default:
      return visibility
  }
}

const resolveArtifactVisibilityFromReport = (report: ReportDefinitionV1): ArtifactVisibility => (
  report.visibility === 'team' ? 'group' : 'private'
)

const buildHighlights = (
  datasetLabel: string,
  columnCount: number,
  rowCount: number,
  visibility: ArtifactVisibility
): string[] => ([
  `Dataset: ${datasetLabel}`,
  `Columns: ${columnCount}`,
  `Rows: ${rowCount}`,
  `Visibility: ${getArtifactVisibilityLabel(visibility)}`
])

const buildSnapshotPreview = (input: PublishReportSnapshotArtifactInput): ReportSnapshotArtifactPreview => {
  const generatedAt = input.generatedAt?.trim() || new Date().toISOString()
  return {
    datasetKey: input.report.datasetKey,
    datasetLabel: input.datasetLabel,
    columns: input.columns.map((column) => ({ ...column })),
    scope: normalizeReportScope(input.report.scope),
    generatedAt,
    rowCount: input.rows.length,
    sampleRows: cloneRows(input.rows.slice(0, 5))
  }
}

const buildLifecycleEvents = (artifact: StoredReportSnapshotArtifact): ArtifactLifecycleEvent[] => {
  const versionEvents = artifact.versions.flatMap((version) => {
    const nextEvents: ArtifactLifecycleEvent[] = []

    if (version.publishedAt) {
      nextEvents.push({
        type: 'published',
        at: version.publishedAt ?? version.generatedAt,
        version: version.version
      })
    }

    if (version.revokedAt) {
      nextEvents.push({
        type: 'revoked',
        at: version.revokedAt,
        version: version.version
      })
    }

    return nextEvents
  })

  if (artifact.archivedAt) {
    versionEvents.push({
      type: 'archived',
      at: artifact.archivedAt,
      version: null
    })
  }

  const eventPriority: Record<ArtifactLifecycleEvent['type'], number> = {
    revoked: 3,
    archived: 2,
    published: 1
  }

  return versionEvents.sort((left, right) => {
    if (left.at === right.at) {
      if ((left.version ?? 0) !== (right.version ?? 0)) {
        return (right.version ?? 0) - (left.version ?? 0)
      }

      return eventPriority[right.type] - eventPriority[left.type]
    }

    return right.at.localeCompare(left.at)
  })
}

const buildDetail = (artifact: StoredReportSnapshotArtifact): ReportSnapshotArtifactDetail => {
  const currentVersion = artifact.versions.find((version) => version.version === artifact.currentVersion) ?? artifact.versions[0]
  const lifecycleEvents = buildLifecycleEvents(artifact)

  return {
    artifactId: artifact.artifactId,
    artifactType: 'report-snapshot',
    overview: {
      title: artifact.title,
      contextLine: artifact.contextLine,
      summary: artifact.summary,
      visibility: artifact.visibility,
      status: artifact.status,
      currentVersion: artifact.currentVersion,
      ownerTenantKey: artifact.ownerTenantKey,
      publishedAt: artifact.publishedAt,
      updatedAt: artifact.updatedAt
    },
    snapshotPreview: currentVersion.snapshotPreview,
    provenance: {
      sourceRefs: [
        {
          domain: 'reports',
          entityId: artifact.sourceReportId,
          label: artifact.title
        }
      ],
      publishedBy: artifact.publisherDisplayName
        ? {
            tenantKey: artifact.ownerTenantKey,
            displayName: artifact.publisherDisplayName
          }
        : null,
      lifecycleEvents
    },
    versions: artifact.versions
      .slice()
      .sort((left, right) => right.version - left.version)
      .map((version) => ({
        version: version.version,
        status: version.status,
        publishedAt: version.publishedAt
      })),
    source: {
      label: 'Open in Reports',
      href: artifact.sourceHref
    },
    actions: {
      canCreateDraft: artifact.status === 'published',
      canRefreshDraftSnapshot: false,
      canPublish: false,
      canRevoke: artifact.status === 'published',
      canArchive: artifact.status !== 'archived'
    }
  }
}

export const listReportSnapshotArtifactSummaries = (tenantKey = getActiveTenantKey()): ReportSnapshotArtifactSummary[] => (
  loadStoredArtifacts(tenantKey).map((artifact) => ({
    artifactId: artifact.artifactId,
    artifactType: artifact.artifactType,
    title: artifact.title,
    contextLine: artifact.contextLine,
    summary: artifact.summary,
    visibility: artifact.visibility,
    status: artifact.status,
    currentVersion: artifact.currentVersion,
    ownerTenantKey: artifact.ownerTenantKey,
    publishedAt: artifact.publishedAt,
    updatedAt: artifact.updatedAt,
    sourceLabel: artifact.sourceLabel,
    sourceHref: artifact.sourceHref,
    highlights: artifact.highlights
  }))
)

export const getReportSnapshotArtifactDetail = (
  artifactId: string,
  tenantKey = getActiveTenantKey()
): ReportSnapshotArtifactDetail | null => {
  const artifact = loadStoredArtifacts(tenantKey).find((candidate) => candidate.artifactId === artifactId)
  return artifact ? buildDetail(artifact) : null
}

export const getReportSnapshotArtifactDetailBySourceReportId = (
  sourceReportId: string,
  tenantKey = getActiveTenantKey()
): ReportSnapshotArtifactDetail | null => {
  const artifact = loadStoredArtifacts(tenantKey).find((candidate) => candidate.sourceReportId === sourceReportId)
  return artifact ? buildDetail(artifact) : null
}

export const revokeReportSnapshotArtifact = (
  artifactId: string,
  tenantKey = getActiveTenantKey()
): ReportSnapshotArtifactDetail | null => {
  const currentItems = loadStoredArtifacts(tenantKey)
  const existing = currentItems.find((artifact) => artifact.artifactId === artifactId)

  if (!existing) {
    return null
  }

  if (existing.status === 'revoked') {
    return buildDetail(existing)
  }

  const currentVersion = existing.versions.find((version) => version.version === existing.currentVersion)
  if (existing.status !== 'published' || currentVersion?.status !== 'published') {
    return buildDetail(existing)
  }

  const now = new Date().toISOString()
  const updatedArtifact: StoredReportSnapshotArtifact = {
    ...existing,
    status: 'revoked',
    updatedAt: now,
    versions: existing.versions.map((version) => (
      version.version === existing.currentVersion
        ? { ...version, status: 'revoked', revokedAt: now }
        : version
    ))
  }

  const nextItems = currentItems.map((artifact) => (
    artifact.artifactId === artifactId ? updatedArtifact : artifact
  ))
  saveArtifacts(nextItems, tenantKey)

  return buildDetail(updatedArtifact)
}

export const archiveReportSnapshotArtifact = (
  artifactId: string,
  tenantKey = getActiveTenantKey()
): ReportSnapshotArtifactDetail | null => {
  const currentItems = loadStoredArtifacts(tenantKey)
  const existing = currentItems.find((artifact) => artifact.artifactId === artifactId)

  if (!existing) {
    return null
  }

  if (existing.status === 'archived') {
    return buildDetail(existing)
  }

  const updatedArtifact: StoredReportSnapshotArtifact = {
    ...existing,
    status: 'archived',
    archivedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }

  const nextItems = currentItems.map((artifact) => (
    artifact.artifactId === artifactId ? updatedArtifact : artifact
  ))
  saveArtifacts(nextItems, tenantKey)

  return buildDetail(updatedArtifact)
}

export const publishReportSnapshotArtifact = (
  input: PublishReportSnapshotArtifactInput
): ReportSnapshotArtifactDetail => {
  const tenantKey = input.tenantKey ?? input.report.tenantKey ?? getActiveTenantKey()
  const currentItems = loadStoredArtifacts(tenantKey)
  const existing = currentItems.find((artifact) => artifact.sourceReportId === input.report.id)
  const now = new Date().toISOString()
  const nextVersionNumber = existing ? existing.currentVersion + 1 : 1
  const snapshotPreview = buildSnapshotPreview(input)
  const visibility = resolveArtifactVisibilityFromReport(input.report)
  const nextVersion: ReportSnapshotArtifactVersion = {
    version: nextVersionNumber,
    status: 'published',
    publishedAt: now,
    revokedAt: null,
    generatedAt: snapshotPreview.generatedAt,
    snapshotPreview
  }

  const previousVersions = (existing?.versions ?? []).map((version) => (
    version.status === 'published'
      ? { ...version, status: 'superseded' as const }
      : version
  ))

  const nextArtifact: StoredReportSnapshotArtifact = {
    version: 1,
    artifactId: existing?.artifactId ?? createArtifactId(input.report.id),
    artifactType: 'report-snapshot',
    title: input.report.name,
    contextLine: buildContextLine(input.report, input.datasetLabel),
    summary: buildSummary(input.datasetLabel, snapshotPreview.rowCount),
    visibility,
    status: 'published',
    currentVersion: nextVersionNumber,
    ownerTenantKey: tenantKey,
    publishedAt: now,
    updatedAt: now,
    sourceLabel: 'Reports',
    sourceHref: input.sourceHref?.trim() || `/reports?reportId=${encodeURIComponent(input.report.id)}`,
    highlights: buildHighlights(input.datasetLabel, input.columns.length, snapshotPreview.rowCount, visibility),
    sourceReportId: input.report.id,
    datasetKey: input.report.datasetKey,
    datasetLabel: input.datasetLabel,
    publisherDisplayName: toTrimmedString(input.publisherDisplayName),
    archivedAt: null,
    versions: previousVersions.concat(nextVersion)
  }

  const nextItems = currentItems.filter((artifact) => artifact.sourceReportId !== input.report.id)
  nextItems.unshift(nextArtifact)
  saveArtifacts(nextItems, tenantKey)

  return buildDetail(nextArtifact)
}